'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Store } from '@eliadmin/core/src/api/general/general-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { getFlagEmoji } from '@eliadmin/core/src/utils/locale-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useState, useEffect } from 'react';
import { useMediaQuery } from '@eliadmin/core/src/hooks/use-media-query';
import { useQuery } from '@tanstack/react-query';
import { fetchStores } from '@eliadmin/core/src/api/general/general-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { usePathname } from 'next/navigation';

export const HeaderLanguageMultistore = ({ isHomepage = false }: { isHomepage: boolean }) => {
  const locale = useLocaleStore(state => state.currentLocale);
  const pathname = usePathname();

  const { data: storesData } = useQuery({
    queryKey: ['stores'],
    queryFn: () => fetchStores({ locale }),
  });

  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const supportsHover = useMediaQuery('(hover: hover)');

  useEffect(() => {
    const store = storesData?.data.find(store => store.is_current) || storesData?.data[0];
    setCurrentStore(store || null);
  }, [storesData]);

  if (!currentStore) {
    return (
      <div className="relative">
        <Button type="button" disabled aria-label="Language / Jazyk" variant={isHomepage ? 'alternative-alt' : 'alternative-default'} size="icon">
          <Spinner />
        </Button>
      </div>
    );
  }

  const handleMouseEnter = () => {
    if (supportsHover) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (supportsHover) {
      setIsHovered(false);
    }
  };

  const getStoreUrl = (storeUrl: string) => {
    const baseUrl = storeUrl.replace(/\/$/, '');
    const currentPath = pathname && pathname !== '/' ? pathname : '';

    return `${baseUrl}${currentPath}`;
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className='relative'
    >
      <Button type="button" aria-label={`Language / Jazyk: ${currentStore.name}`} aria-expanded={isHovered} onClick={() => setIsHovered(prev => !prev)} onKeyDown={event => { if (event.key === 'Escape') setIsHovered(false); }} variant={isHomepage ? 'alternative-alt' : 'alternative-default'} size={'icon'}>
        <Image
          src={currentStore?.language.image.url || '/images/placeholder.png'}
          alt={resolveImageAlt(currentStore?.language?.image, 'alt')}
          className={cn('border border-border', 'ea-language-select__flag')}
          width={22}
          height={18}
        />
      </Button>
      {isHovered && (
        <div
          className="absolute right-0 top-[40px] mt-2 w-auto rounded-[12px] bg-white/50 p-1 shadow-xl"
          style={{ zIndex: 1000 }}
        >
          <div className="flex flex-col items-start gap-1">
            {storesData?.data.map(store => {
              if (store.is_current) {
                return null;
              }

              return (
                <Button
                  key={store.language.locale}
                  variant="outline"
                  size="sm"
                  className="mb-1 flex w-full min-w-fit justify-start whitespace-nowrap border-none p-0 px-2 text-left font-semibold shadow-none outline-none"
                  asChild
                >
                  <Link href={getStoreUrl(store.url)}>
                    {store.language.image ? (
                      <div className="flex min-w-fit flex-row pr-8">
                        <Image
                          src={store.language.image.url}
                          alt={resolveImageAlt(store?.language?.image, store?.language?.locale)}
                          className={cn('mr-2', 'ea-language-select__flag')}
                          width={25}
                          height={20}
                        />
                        <p className='text-sm'>{store.name}</p>
                      </div>
                    ) : (
                      <>
                        <span className={cn('pr-2', 'ea-language-select__flag')}>
                          {getFlagEmoji(store.language.locale)}
                        </span>
                        {store.name}
                      </>
                    )}
                  </Link>
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
